import { useState, useEffect } from "react";
import * as Yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { Offcanvas, Form, Button, Row, Col } from "react-bootstrap";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import {
  updateUser,
  searchDepartment,
  searchDesignation,
  searchLocation,
} from "../../services/UserService";
import BarLoader from "../../components/Loader/BarLoader";
import { roleDisplayMap } from "../../utilities";
import { searchAreaByDepartmentApi } from "../../services/SearchService";
import { values } from "lodash";
import Select from "react-select";

const userValidationSchema = Yup.object({
  firstName: Yup.string().required("First Name is required"),
  lastName: Yup.string().required("Last Name is required"),
  email: Yup.string()
    .required("Email is required")
    .email("Invalid email format"),
  mobileNumber: Yup.string()
    .required("Mobile number is required")
    .matches(/^[0-9]+$/, "Mobile number must be 10 digits")
    .length(10, "Mobile number must be exactly 10 digits"),
  role: Yup.string().required("Role is required"),
  is_active: Yup.boolean(),

  area: Yup.array().when("role", {
    is: (val) => val !== "AUDITOR",
    then: (schema) =>
      schema
        .of(Yup.string())
        .min(1, "At least one area is required")
        .required("Area is required"),
    otherwise: (schema) => schema.strip(),
  }),

  location: Yup.string().when("role", {
    is: (val) => val !== "AUDITOR",
    then: (schema) => schema.required("Location is required"),
    otherwise: (schema) => schema.strip(),
  }),

  department: Yup.string().when("role", {
    is: (val) => val !== "AUDITOR",
    then: (schema) => schema.required("Department is required"),
    otherwise: (schema) => schema.strip(),
  }),

  designation: Yup.string().when("role", {
    is: (val) => val !== "AUDITOR",
    then: (schema) => schema.required("Designation is required"),
    otherwise: (schema) => schema.strip(),
  }),
});


function EditUser({
  showEditOffcanvas,
  setShowEditOffcanvas,
  userData,
  onUserUpdated,
}) {
  const [updating, setUpdating] = useState(false);
  const [areas, setAreas] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [isDepartmentLoading, setIsDepartmentLoading] = useState(false);
  const [isAreaLoading, setIsAreaLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(userValidationSchema),
  });

  const selectedLocation = watch("location");
  const selectedDepartment = watch("department");
  const selectedRole = watch("role");

  const onSubmit = async (data) => {
    try {
      const payload = {
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        mobile_number: data.mobileNumber,
        role: data.role,
        is_active: data.is_active.toString(),
      };

      if (data.role !== "AUDITOR") {
        payload.area = Array.isArray(data.area) ? data.area : [data.area];
        payload.location = data.location;
        payload.designation = data.designation;
        payload.department = data.department;
      }

      setUpdating(true);
      const response = await updateUser(data.id, payload);

      if (onUserUpdated) {
        onUserUpdated({
          ...response.data.results,
          location_id: data.location,
          department_id: data.department,
          designation_id: data.designation,
          is_active: data.is_active,
        });
      }

      toast.success("User updated successfully");
      setShowEditOffcanvas(false);
    } catch (error) {
      console.error("Update error:", error);
      toast.error(error.response?.data?.error || "Failed to update user");
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    const initializeForm = async () => {
      try {
        setLoading(true);

        const [locRes, desigRes] = await Promise.all([
          searchLocation(""),
          searchDesignation(""),
        ]);

        setLocations(
          locRes.data.results.map((loc) => ({
            value: loc.value,
            label: loc.label,
          }))
        );

        setDesignations(
          desigRes.data.results.map((desig) => ({
            value: desig.value,
            label: desig.label,
          }))
        );

        if (userData) {
          if (userData.location_id) {
            const deptRes = await searchDepartment(userData.location_id);
            setDepartments(
              deptRes.data.results.map((dept) => ({
                value: dept.value,
                label: dept.label,
              }))
            );
          }

          if (userData.department_id) {
            const areaRes = await searchAreaByDepartmentApi(
              userData.department_id
            );
            setAreas(
              areaRes.data.results.map((area) => ({
                value: area.value,
                label: area.label,
              }))
            );
          }

          reset({
            id: userData.id,
            firstName: userData.first_name,
            lastName: userData.last_name,
            email: userData.email,
            mobileNumber: userData.mobile_number,
            area: userData.area_id || [], // <- Set multiple selected values
            location: userData.location_id,
            designation: userData.designation_id,
            department: userData.department_id,
            role: userData.role,
            is_active: userData.is_active,
          });
        }
      } catch (error) {
        console.error("Initialization error:", error);
        toast.error("Failed to load form data");
      } finally {
        setLoading(false);
      }
    };

    initializeForm();
  }, [userData, reset]);

  useEffect(() => {
    if (selectedLocation) {
      fetchDepartmentsByLocation(selectedLocation);
    } else {
      setDepartments([]);
      setValue("department", "");
    }
  }, [selectedLocation, setValue]);

  const fetchAreaByDepartment = async (departmentId) => {
    try {
      const response = await searchAreaByDepartmentApi(departmentId);
      console.log("response", response);
      setAreas(
        response.data?.results.map((area) => ({
          value: area.value,
          label: area.label,
        }))
      );
    } catch (error) {
      console.error("Error fetching areas:", error);
      toast.error("Failed to load areas");
    }
  };

  const fetchDepartmentsByLocation = async (locationId) => {
    try {
      setIsAreaLoading(true);
      const response = await searchDepartment(locationId);
      setDepartments(
        response.data.results.map((dept) => ({
          value: dept.value,
          label: dept.label,
        }))
      );
    } catch (error) {
      console.error("Failed to fetch departments:", error);
      toast.error("Failed to load departments");
    } finally {
      setIsAreaLoading(false);
    }
  };

  const handleDiscard = () => {
    setShowEditOffcanvas(false);
    reset();
  };

  if (loading) {
    return (
      <Offcanvas
        show={showEditOffcanvas}
        onHide={handleDiscard}
        placement="end"
        className="w-40"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Edit User</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="d-flex justify-content-center align-items-center">
          <BarLoader />
        </Offcanvas.Body>
      </Offcanvas>
    );
  }

  return (
    <Offcanvas
      show={showEditOffcanvas}
      onHide={handleDiscard}
      placement="end"
      className="w-40"
    >
      <Offcanvas.Header closeButton className="pb-0">
        <Offcanvas.Title>Edit User</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row className="mb-2">
            <Col md={6}>
              <Form.Group>
                <Form.Label>First Name</Form.Label>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <Form.Control
                      type="text"
                      placeholder="First Name"
                      isInvalid={!!errors.firstName}
                      {...field}
                    />
                  )}
                />
                {errors.firstName && (
                  <div className="text-danger small">
                    {errors.firstName.message}
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Last Name</Form.Label>
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <Form.Control
                      type="text"
                      placeholder="Last Name"
                      isInvalid={!!errors.lastName}
                      {...field}
                    />
                  )}
                />
                {errors.lastName && (
                  <div className="text-danger small">
                    {errors.lastName.message}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-2">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Email</Form.Label>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <Form.Control
                      type="email"
                      placeholder="Email"
                      isInvalid={!!errors.email}
                      {...field}
                    />
                  )}
                />
                {errors.email && (
                  <div className="text-danger small">
                    {errors.email.message}
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Mobile Number</Form.Label>
                <Controller
                  name="mobileNumber"
                  control={control}
                  render={({ field }) => (
                    <Form.Control
                      type="text"
                      placeholder="Mobile Number"
                      isInvalid={!!errors.mobileNumber}
                      {...field}
                    />
                  )}
                />
                {errors.mobileNumber && (
                  <div className="text-danger small">
                    {errors.mobileNumber.message}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-2">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Role</Form.Label>
                <Controller
                  name="role"
                  control={control}
                  render={({ field }) => (
                    <Select
                      inputId="role"
                      placeholder="Select Role"
                      isClearable
                      options={Object.entries(roleDisplayMap).map(
                        ([value, label]) => ({
                          value,
                          label,
                        })
                      )}
                      value={
                        field.value
                          ? {
                              value: field.value,
                              label: roleDisplayMap[field.value],
                            }
                          : null
                      }
                      onChange={(selectedOption) =>
                        field.onChange(
                          selectedOption ? selectedOption.value : ""
                        )
                      }
                      classNamePrefix="react-select"
                    />
                  )}
                />
                {errors.role && (
                  <div className="text-danger small">{errors.role.message}</div>
                )}
              </Form.Group>
            </Col>

            {selectedRole !== "AUDITOR" && (
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Location</Form.Label>
                  <Controller name="location" control={control} render={({ field }) => (
                    <Select
                      inputId="location"
                      placeholder="Select Location"
                      isClearable
                      options={locations}
                      value={locations.find(opt => opt.value === field.value) || null}
                      onChange={(selectedOption) => {
                        const value = selectedOption ? selectedOption.value : "";
                        field.onChange(value);
                        fetchDepartmentsByLocation(value);
                      }}
                      classNamePrefix="react-select"
                    />
                  )} />
                  {errors.location && <div className="text-danger small">{errors.location.message}</div>}
                </Form.Group>
              </Col>
            )}
          </Row>
          
          {selectedRole !== "AUDITOR" && (
            <>
              <Row className="g-3 align-items-end">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Designation</Form.Label>
                    <Controller
                      name="designation"
                      control={control}
                      render={({ field }) => (
                        <Select
                          inputId="designation"
                          placeholder="Select Designation"
                          isClearable
                          options={designations}
                          value={
                            designations.find((opt) => opt.value === field.value) ||
                            null
                          }
                          onChange={(selectedOption) =>
                            field.onChange(
                              selectedOption ? selectedOption.value : ""
                            )
                          }
                          classNamePrefix="react-select"
                        />
                      )}
                    />
                    {errors.designation && (
                      <div className="text-danger small">
                        {errors.designation.message}
                      </div>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Department</Form.Label>
                    <Controller
                      name="department"
                      control={control}
                      render={({ field }) => (
                        <Select
                          inputId="department"
                          placeholder="Select Department"
                          isClearable
                          isDisabled={!selectedLocation}
                          options={departments}
                          value={
                            departments.find((opt) => opt.value === field.value) ||
                            null
                          }
                          onChange={(selectedOption) =>
                            field.onChange(
                              selectedOption ? selectedOption.value : ""
                            )
                          }
                          classNamePrefix="react-select"
                        />
                      )}
                    />
                    {errors.department && (
                      <div className="text-danger small">
                        {errors.department.message}
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row className="g-3 align-items-end mt-0">
                <Col md={12 }>
                  <Form.Group>
                    <Form.Label>Area</Form.Label>
                    <Controller
                      name="area"
                      control={control}
                      render={({ field }) => (
                        <Select
                          inputId="area"
                          placeholder="Select Area(s)"
                          isMulti
                          isLoading={isAreaLoading}
                          options={areas}
                          value={areas.filter((opt) =>
                            field.value?.includes(opt.value)
                          )}
                          onMenuOpen={() => {
                            if (selectedDepartment)
                              fetchAreaByDepartment(selectedDepartment);
                          }}
                          onChange={(selectedOptions) =>
                            field.onChange(selectedOptions.map((opt) => opt.value))
                          }
                          classNamePrefix="react-select"
                        />
                      )}
                    />
                    {errors.area && (
                      <div className="text-danger small">{errors.area.message}</div>
                    )}
                  </Form.Group>
                </Col>

                
              </Row>

              <Row >
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>&nbsp;</Form.Label>
                    <Controller
                      name="is_active"
                      control={control}
                      render={({ field }) => (
                        <Form.Check
                          type="switch"
                          label="Active User"
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      )}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}

          {/* Buttons */}
          <Row className="mt-3">
            <Col className="d-flex justify-content-end flex-wrap gap-2">
              <Button
                variant="outline-danger"
                style={{ padding: "4px 14px", lineHeight: "2" }}
                onClick={handleDiscard}
                className="flex-shrink-0"
              >
                Discard
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={updating}
                style={{ padding: "4px 14px", lineHeight: "2" }}
                className="flex-shrink-0"
              >
                {updating ? (
                  <BarLoader height={14} width={80} />
                ) : (
                  "Update"
                )}
              </Button>
            </Col>
          </Row>
        </Form>
      </Offcanvas.Body>
    </Offcanvas>
  );
}

export default EditUser;
